/* eslint-disable @typescript-eslint/no-unused-vars */
import { Button, Modal, ModalBody, ModalFooter, Nav, NavItem, NavLink } from "reactstrap";
import { IPrompt, promptsService } from "../../../services/prompts";
import {
  ButtonContainer,
  ContentContainer,
  ModalHeaderButton,
  ModalHeaderCreatPrompt,
  ModalHeaderTitle,
  PromptBar,
  TabContainer,
  TextPrompt,
} from "./styled";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { actionSetInputText, selectorChatId } from "../../../redux/chatSlice";
import SimpleBar from "simplebar-react";
import { selectorProjectById, selectorProjects } from "../../../redux/projectSlice";
import { CreateModalAgentPrompt } from "../NewAgentPromptModal";
import PromptItem from "./promptItem/promptItem";
import { selectIsAdminOrSuperUser } from "../../../redux/authenticationSlice";

interface TogglePromptCatalogProps {
  ModalToggle: boolean;
  setModalToggle: (value: boolean) => void;
}

const PromptCatalog: React.FC<TogglePromptCatalogProps> = ({ ModalToggle, setModalToggle }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const selectedProject = useAppSelector(selectorProjectById);
  const [value, setValue] = useState(1);
  const [textValue, setTextValue] = useState("");
  const [selectedPrompt, setSelectedPrompt] = useState<IPrompt | null>(null);
  const [ownPrompts, setOwnPrompts] = useState<IPrompt[]>([]);
  const [agentPrompts, setAgentPrompts] = useState<IPrompt[]>([]);
  const [openAgentPromptModal, setOpenAgentPromptModal] = useState(false);
  const [projectId, setProjectId] = useState<number | undefined>(undefined);

  const is_admin = useAppSelector(selectIsAdminOrSuperUser);

  useEffect(() => {
    (async () => {
      if (selectedProject) {
        const _ownPrompts = await promptsService.getOwn();
        const _agentPrompts = await promptsService.prompt_in_a_project(selectedProject.id);

        setAgentPrompts(_agentPrompts);
        setOwnPrompts(_ownPrompts);
      }
    })();
  }, [selectedProject]);

  const handleClose = () => {
    setModalToggle(false);
  };

  const applyPrompt = () => {
    dispatch(actionSetInputText(selectedPrompt?.prompt || ""));
    setModalToggle(false);
  };

  const handleChange = (index: number) => {
    if (index === 2) {
      setProjectId(selectedProject?.id);
    } else {
      setProjectId(undefined);
    }
    setValue(index);
    setSelectedPrompt(null);
  };

  const handleSelectPrompt = (prompt: IPrompt) => {
    setSelectedPrompt(prompt);
  };

  useEffect(() => {
    setTextValue(selectedPrompt?.prompt || "");
  }, [selectedPrompt]);

  const addToPrompts = (newPrompt: IPrompt) => {
    if (projectId) {
      setAgentPrompts((prev) => (prev ? [newPrompt, ...prev] : [newPrompt]));
    } else {
      setOwnPrompts((prev) => (prev ? [newPrompt, ...prev] : [newPrompt]));
    }
  };

  const handleDeletePrompt = async (promptId: number) => {
    const _ownPrompts = await promptsService.getOwn();
    setOwnPrompts(_ownPrompts);
    setTextValue("");
  };

  const handleUpdatePrompt = (updatedPrompt: IPrompt) => {
    setOwnPrompts((prevPrompts) =>
      prevPrompts.map((prompt) => (prompt.id === updatedPrompt.id ? updatedPrompt : prompt)),
    );
    setAgentPrompts((prevPrompts) =>
      prevPrompts.map((prompt) => (prompt.id === updatedPrompt.id ? updatedPrompt : prompt)),
    );
  };

  return (
    <>
      <CreateModalAgentPrompt
        onAddPrompt={addToPrompts}
        projectId={projectId}
        ModalToggle={openAgentPromptModal}
        setModalToggle={setOpenAgentPromptModal}
        onConfirm={() => {
          setOpenAgentPromptModal(false);
        }}
      />
      <Modal size="lg" isOpen={ModalToggle} toggle={handleClose} centered>
        <ModalHeaderCreatPrompt>
          <ModalHeaderTitle>{t("pages.prompts.components.promptCatalog.title")}</ModalHeaderTitle>
          <ModalHeaderButton onClick={handleClose}>×</ModalHeaderButton>
        </ModalHeaderCreatPrompt>
        <ModalBody>
          <ContentContainer>
            <TabContainer>
              <Nav className="nav-tabs-custom nav-primary">
                <NavItem>
                  <NavLink
                    style={{ cursor: "pointer" }}
                    className={value === 2 ? "active" : ""}
                    onClick={() => handleChange(2)}
                  >
                    {t("pages.prompts.components.promptCatalog.tabAgentPrompts")}
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    style={{ cursor: "pointer" }}
                    className={value === 1 ? "active" : ""}
                    onClick={() => handleChange(1)}
                  >
                    {t("pages.prompts.components.promptCatalog.tabMyPrompts")}
                  </NavLink>
                </NavItem>
              </Nav>
            </TabContainer>
            <ButtonContainer>
              <Button
                color="light"
                onClick={() => {
                  setOpenAgentPromptModal(true);
                }}
                disabled={value == 2 && !is_admin}
              >
                {value == 2 ? "+ Criar prompt do agente" : "+ Criar prompt pessoal"}
                {/* {t("pages.prompts.components.promptCatalog.btnNewPrompt")} */}
              </Button>
            </ButtonContainer>
          </ContentContainer>
          <ContentContainer>
            <PromptBar>
              {value === 1 ? (
                <div>
                  {ownPrompts.map((prompt) => (
                    <PromptItem
                      key={prompt.id}
                      showEditDeleteButtons={true}
                      prompt={prompt}
                      selected={selectedPrompt?.id === prompt.id}
                      onSelect={handleSelectPrompt}
                      onUpdatePrompt={handleUpdatePrompt}
                      onDeletePrompt={handleDeletePrompt}
                    />
                  ))}
                </div>
              ) : (
                <div>
                  {agentPrompts.map((prompt) => (
                    <PromptItem
                      key={prompt.id}
                      showEditDeleteButtons={false}
                      prompt={prompt}
                      selected={selectedPrompt?.id === prompt.id}
                      onSelect={handleSelectPrompt}
                      onUpdatePrompt={handleUpdatePrompt}
                      onDeletePrompt={handleDeletePrompt}
                    />
                  ))}
                </div>
              )}
            </PromptBar>
            <TextPrompt value={textValue} readOnly></TextPrompt>
          </ContentContainer>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={handleClose}>
            {t("pages.prompts.components.promptCatalog.btnCancel")}
          </Button>
          <Button color="primary" onClick={applyPrompt}>
            {t("pages.prompts.components.promptCatalog.btnApplyOnConversation")}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export { PromptCatalog };
